import type {
  BenefitDesignPayStructure,
  BenefitDesignQuoteGroup,
} from '@/features/shop-insurance/types';
import { Quote } from '@/model/quotesModel';
import { getMultiplierForPremium } from '@/util/lincolnPricingUtils';
import { getOAMultipliersForPremium, isOneAmericaAssetCare } from '@/util/oneAmericaPricingUtils';
import { adjustQuoteForPremium } from '@/util/quoteAdjustment';
import { getInflationProtectionYears, getInflationRateFromQuote } from '@/utils/inflationParser';
import { FundingPolicyType } from '@shared';
import { calcFutureValue } from '@shared/calculations/utils/calcFutureValue';
import { calcFutureValueSimpleInflation } from '@shared/calculations/utils/calcFutureValueSimpleInflation';

/**
 * Reverse of getScalingFactorForQuote in util/quoteAdjustment.ts.
 *
 * For linear carriers:
 *   targetPremium = originalPremium * (targetBenefit / originalBenefit)
 *
 * For non-linear carriers (MoneyGuard, OneAmerica AssetCare cat-1/cat-3), binary search
 * finds the premium P such that getScalingFactor(quote, P) * originalBenefit ≈ targetBenefit.
 *
 * Returns null if the required premium falls outside [investmentMin, investmentMax].
 */
export function reverseInterpolatePremium(
  quote: Quote,
  targetMonthlyBenefit: number,
): number | null {
  if (!quote.ltcBenefitMonthlyIndividual || quote.ltcBenefitMonthlyIndividual <= 0) {
    return null;
  }

  const isMoneyGuard = quote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage;
  const isNonLinearOA = isOneAmericaAssetCare(quote.fundingPolicyType);

  if (isMoneyGuard || isNonLinearOA) {
    return binarySearchPremium(quote, targetMonthlyBenefit);
  }

  // Linear carriers: simple ratio
  const targetPremium = Math.round(
    quote.premiumAmount * (targetMonthlyBenefit / quote.ltcBenefitMonthlyIndividual),
  );

  // Skip bounds check when the target premium is effectively the original premium
  // (i.e. the requested benefit matches the DB benefit). Some carriers store bounds
  // that don't encompass their own premiumAmount, so an exact-match should never be rejected.
  const isOriginalPremium = Math.abs(targetPremium - quote.premiumAmount) < 1;
  if (!isOriginalPremium) {
    const min = quote.investmentMin;
    const max = quote.investmentMax;
    if (min && targetPremium < min) return null;
    if (max && targetPremium > max) return null;
  }
  return targetPremium;
}

function getScalingFactor(quote: Quote, newPremium: number): number {
  const linearFactor = newPremium / quote.premiumAmount;

  if (quote.fundingPolicyType === FundingPolicyType.moneyGuardFixedAdvantage) {
    const originalMultiplier = getMultiplierForPremium(quote.premiumAmount);
    const newMultiplier = getMultiplierForPremium(newPremium);
    return linearFactor * (newMultiplier / originalMultiplier);
  }

  if (isOneAmericaAssetCare(quote.fundingPolicyType)) {
    const original = getOAMultipliersForPremium(
      quote.fundingPolicyType,
      quote.productFundingOption,
      quote.aobInflation,
      quote.cobInflation,
      quote.cobDuration,
      quote.premiumAmount,
      quote.usaStateCategory,
      quote.primaryAge,
      quote.primaryGender,
      quote.aobDuration,
      quote.secondaryAge,
    );
    const target = getOAMultipliersForPremium(
      quote.fundingPolicyType,
      quote.productFundingOption,
      quote.aobInflation,
      quote.cobInflation,
      quote.cobDuration,
      newPremium,
      quote.usaStateCategory,
      quote.primaryAge,
      quote.primaryGender,
      quote.aobDuration,
      quote.secondaryAge,
    );
    return linearFactor * (target.db / original.db);
  }

  return linearFactor;
}

function binarySearchPremium(quote: Quote, targetMonthlyBenefit: number): number | null {
  const min = quote.investmentMin ?? quote.premiumAmount * 0.5;
  const max = quote.investmentMax ?? quote.premiumAmount * 2;
  const tolerance = 1;
  let lo = min;
  let hi = max;

  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    const scaledBenefit = getScalingFactor(quote, mid) * quote.ltcBenefitMonthlyIndividual!;

    if (Math.abs(scaledBenefit - targetMonthlyBenefit) < tolerance) {
      return Math.round(mid);
    }

    if (scaledBenefit < targetMonthlyBenefit) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  const finalPremium = (lo + hi) / 2;
  const finalBenefit = getScalingFactor(quote, finalPremium) * quote.ltcBenefitMonthlyIndividual!;
  if (Math.abs(finalBenefit - targetMonthlyBenefit) > tolerance) {
    return null;
  }
  return Math.round(finalPremium);
}

/**
 * For each filtered quote, compute the premium required to deliver the target monthly benefit.
 * Returns adjusted quotes with the new premium and scaled fields.
 * Quotes where the required premium is out of bounds are excluded.
 */
export function reverseInterpolateQuotes(quotes: Quote[], targetMonthlyBenefit: number): Quote[] {
  const result: Quote[] = [];

  for (const quote of quotes) {
    const targetPremium = reverseInterpolatePremium(quote, targetMonthlyBenefit);
    if (targetPremium === null) continue;

    const adjusted = adjustQuoteForPremium(quote, targetPremium);
    result.push(pinMonthlyBenefitToTarget(adjusted, targetMonthlyBenefit));
  }

  return result;
}

/**
 * Pins the monthly benefit to the exact target value. All other fields
 * (death benefit, total LTC, etc.) are left as-is from adjustQuoteForPremium
 * because they legitimately differ per payment option.
 */
function pinMonthlyBenefitToTarget(adjusted: Quote, targetMonthlyBenefit: number): Quote {
  return {
    ...adjusted,
    ltcBenefitMonthlyIndividual: targetMonthlyBenefit,
  };
}

export function calcProjectedMonthlyBenefit(quote: Quote, yearsUntilLtc: number): number | null {
  if (!quote.ltcBenefitMonthlyIndividual || yearsUntilLtc <= 0) return null;

  let inflationRate = getInflationRateFromQuote(quote);

  // cobInflation fallback for OneAmerica quotes where aobInflation is 0
  if (inflationRate <= 0 && quote.cobInflation && quote.cobInflation !== 'None') {
    const cobRate = parseFloat(quote.cobInflation);
    if (!isNaN(cobRate) && cobRate > 0) inflationRate = cobRate;
  }

  if (inflationRate <= 0) return Math.round(quote.ltcBenefitMonthlyIndividual);

  const protectionYears = getInflationProtectionYears(quote);
  const effectiveYears = protectionYears ? Math.min(yearsUntilLtc, protectionYears) : yearsUntilLtc;

  const isSimple = quote.inflationBenefitOption?.toLowerCase().includes('simple') ?? false;
  const rate = inflationRate / 100;

  const projected = isSimple
    ? calcFutureValueSimpleInflation(quote.ltcBenefitMonthlyIndividual, rate, effectiveYears)
    : calcFutureValue(quote.ltcBenefitMonthlyIndividual, rate, effectiveYears);

  return Math.round(projected);
}

/**
 * Returns a clean human-readable pay label, whether the premium is annual (multi-pay),
 * and the number of payment years (null for lump sum or unknown lifetime pay).
 */
export function getPayStructureLabel(quote: Quote): {
  label: string;
  isAnnual: boolean;
  payYears: number | null;
} {
  const raw = (quote.cobPaymentOption ?? quote.productFundingOption ?? '').toLowerCase();

  if (raw.includes('single') || raw.includes('lump'))
    return { label: 'Lump Sum', isAnnual: false, payYears: null };
  if (raw.includes('5')) return { label: '5-Pay', isAnnual: true, payYears: 5 };
  if (raw.includes('10')) return { label: '10-Pay', isAnnual: true, payYears: 10 };
  if (raw.includes('20')) return { label: '20-Pay', isAnnual: true, payYears: 20 };
  if (raw.includes('pay to') || raw.includes('lifetime') || raw.includes('95'))
    return { label: 'Lifetime Pay', isAnnual: true, payYears: null };
  if (raw.includes('annual')) return { label: 'Annual', isAnnual: true, payYears: null };
  if (raw.includes('recurring')) return { label: 'Recurring', isAnnual: true, payYears: null };

  const display = quote.cobPaymentOption ?? quote.productFundingOption ?? '';
  return { label: display, isAnnual: false, payYears: null };
}

/**
 * Groups a flat list of reverse-interpolated quotes by carrier+product,
 * collecting all pay structures per group.
 * Groups are sorted by cheapest pay structure ascending.
 */
export function groupQuotesForBenefitDesign(quotes: Quote[]): BenefitDesignQuoteGroup[] {
  const groupMap = new Map<string, BenefitDesignQuoteGroup>();

  for (const quote of quotes) {
    // Group by carrier + product + inflation variant + risk class.
    // cobPaymentOption / productFundingOption are the pay-structure dimension — they vary *within* a group.
    const aobInfl = quote.aobInflation && quote.aobInflation !== 'None' ? quote.aobInflation : '';
    const cobInfl = quote.cobInflation && quote.cobInflation !== 'None' ? quote.cobInflation : '';
    const inflation =
      quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None'
        ? quote.inflationBenefitOption
        : `${aobInfl}/${cobInfl}`;
    const riskClass = quote.riskClass ?? '';
    const aobDuration = quote.aobDuration ?? '';
    const cobDuration = quote.cobDuration ?? '';
    const durationMonths = quote.ltcBenefitDurationMonths ?? 0;
    const key = `${quote.carrierName}:::${quote.productName}:::${inflation}:::${riskClass}:::${aobDuration}:::${cobDuration}:::${durationMonths}`;

    if (!groupMap.has(key)) {
      groupMap.set(key, {
        carrierName: quote.carrierName,
        productName: quote.productName,
        inflationLabel: inflation,
        riskClass,
        payStructures: [],
        representativeQuote: quote,
      });
    }

    const group = groupMap.get(key)!;

    // Keep the quote with the highest death benefit as the representative
    if ((quote.deathBenefitInitial ?? 0) > (group.representativeQuote.deathBenefitInitial ?? 0)) {
      group.representativeQuote = quote;
    }

    const { label: payLabel, isAnnual, payYears } = getPayStructureLabel(quote);
    const payStructure: BenefitDesignPayStructure = {
      insuranceQuoteId: quote.insuranceQuoteId,
      payLabel,
      isAnnual,
      payYears,
      premiumAmount: quote.premiumAmount,
      productFundingOption: quote.productFundingOption ?? '',
      originalQuote: quote,
    };

    group.payStructures.push(payStructure);
  }

  return Array.from(groupMap.values())
    .map(group => {
      // Deduplicate: keep only the cheapest option per pay label.
      const cheapestByLabel = new Map<string, BenefitDesignPayStructure>();
      for (const ps of group.payStructures) {
        const existing = cheapestByLabel.get(ps.payLabel);
        if (!existing || ps.premiumAmount < existing.premiumAmount) {
          cheapestByLabel.set(ps.payLabel, ps);
        }
      }

      return {
        ...group,
        payStructures: Array.from(cheapestByLabel.values()).sort(
          (structureA, structureB) => structureA.premiumAmount - structureB.premiumAmount,
        ),
      };
    })
    .sort((groupA, groupB) => {
      const getSort = (group: BenefitDesignQuoteGroup) => {
        const tenPay = group.payStructures.find(ps => ps.payLabel === '10-Pay');
        if (tenPay) return tenPay.premiumAmount;
        return group.payStructures[0]?.premiumAmount ?? 0;
      };
      return getSort(groupA) - getSort(groupB);
    });
}
